import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { isRouteTitleOwned } from '@/lib/seo';

import { type Brand, DEFAULT_BRAND } from './brand';

const BrandContext = createContext<Brand>(DEFAULT_BRAND);

interface BrandProviderProps {
  children: React.ReactNode;
  /** Override for tests and Storybook. */
  override?: Brand;
}

export function BrandProvider({ children, override }: BrandProviderProps): JSX.Element {
  const [brand, setBrand] = useState<Brand>(override ?? DEFAULT_BRAND);

  useEffect(() => {
    if (override) return;
    let cancelled = false;
    void resolveBrand().then((resolved) => {
      if (!cancelled && resolved) setBrand(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [override]);

  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary', brand.primaryColor);
    document.documentElement.style.setProperty('--brand-accent', brand.accentColor);
    // Only set the brand-name title when no route <Seo> has claimed it, so
    // per-page SEO titles aren't clobbered (e.g. marketing pages).
    if (!isRouteTitleOwned()) document.title = brand.displayName;
  }, [brand]);

  const value = useMemo(() => brand, [brand]);
  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand(): Brand {
  return useContext(BrandContext);
}

async function resolveBrand(): Promise<Brand | null> {
  try {
    const res = await fetch(`/api/v1/branding/resolve?host=${encodeURIComponent(location.host)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<Brand>;
    return {
      displayName: data.displayName ?? DEFAULT_BRAND.displayName,
      logoUrl: data.logoUrl ?? null,
      primaryColor: data.primaryColor ?? DEFAULT_BRAND.primaryColor,
      accentColor: data.accentColor ?? DEFAULT_BRAND.accentColor,
      supportEmail: data.supportEmail ?? DEFAULT_BRAND.supportEmail,
      legalName: data.legalName ?? DEFAULT_BRAND.legalName,
    };
  } catch {
    return null;
  }
}
